import React, { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { MicrosoftIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import SplaCatalogTab from './SplaCatalogTab';
import SplaConsumptionTab from './SplaConsumptionTab';
import SplaUsageTab from './SplaUsageTab';
import SplaDeviceCoresTab from './SplaDeviceCoresTab';
import SplaBindingsTab from './SplaBindingsTab';
import SplaCandidatesTab from './SplaCandidatesTab';

type SplaTabKey = 'catalog' | 'consumption' | 'usage' | 'device-cores' | 'bindings' | 'candidates';

/**
 * Microsoft SPLA reporting — the Ancalagon-staff-only screen behind the monthly license-consumption
 * report sent to the distributor (Econocom). Six tabs over the /admin/spla/* API families:
 * the reporting-sheet catalog (+ sale pricing), the current per-subject consumption, the immutable
 * monthly usage snapshots, and the compute layer (device-core registry, bindings, and the agent
 * discovery queue).
 *
 * The admin SPLA API is client-agnostic — staff pick the client explicitly in bodies and filters;
 * impersonation plays no part here (the tenant surface is the Datacenter → Licenses page).
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types /admin/spla directly gets <NotFound />
 *      — the same response as a non-existent route, so the admin area's existence is never leaked.
 *      Every tab defers its fetches on the same check, so no admin call ever fires for non-staff.
 *      The backend /admin routes enforce this authoritatively; this is the frontend mirror.
 *
 * Cross-tab freshness: the compute layer maintains `computed` consumption rows, so mutations on one
 * tab change what another shows. Each affected tab takes a `refreshKey` its siblings bump through
 * the callbacks below; tabs are mounted lazily (mountOnEnter) and refetch when their key changes.
 */
function Spla() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;

  const [activeTab, setActiveTab] = useState<SplaTabKey>('catalog');

  // Invalidation counters: bumped by a sibling tab whose mutation made this tab's list stale.
  const [consumptionKey, setConsumptionKey] = useState(0);
  const [bindingsKey, setBindingsKey] = useState(0);
  const [candidatesKey, setCandidatesKey] = useState(0);

  const bumpConsumption = useCallback(() => setConsumptionKey((k) => k + 1), []);
  const bumpBindings = useCallback(() => setBindingsKey((k) => k + 1), []);
  const bumpCandidates = useCallback(() => setCandidatesKey((k) => k + 1), []);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Ancalagon-staff-only. Treat anything other than an explicit `true` as "no access" and render
  // the 404 page so the route reveals nothing about the admin area's existence.
  if (!isStaff) {
    return <NotFound />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>SPLA</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <MicrosoftIcon />
          </Icon>
          Microsoft SPLA reporting
        </Content>
        <Content component="p">
          Manage the distributor license catalog, customer sale prices, and the per-subject consumption
          behind the monthly SPLA report.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => setActiveTab(tabKey as SplaTabKey)}
          aria-label="SPLA management sections"
          mountOnEnter
        >
          <Tab eventKey="catalog" title={<TabTitleText>Catalog</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SplaCatalogTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="consumption" title={<TabTitleText>Consumption</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SplaConsumptionTab isStaff={isStaff} refreshKey={consumptionKey} />
            </div>
          </Tab>
          <Tab eventKey="usage" title={<TabTitleText>Usage reports</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SplaUsageTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="device-cores" title={<TabTitleText>Device cores</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SplaDeviceCoresTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="bindings" title={<TabTitleText>Bindings</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SplaBindingsTab
                isStaff={isStaff}
                refreshKey={bindingsKey}
                onConsumptionChanged={bumpConsumption}
                onCandidatesChanged={bumpCandidates}
              />
            </div>
          </Tab>
          <Tab eventKey="candidates" title={<TabTitleText>Candidates</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <SplaCandidatesTab
                isStaff={isStaff}
                refreshKey={candidatesKey}
                onBindingsChanged={bumpBindings}
                onConsumptionChanged={bumpConsumption}
              />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export { Spla };
